import { ReactNode } from 'react';

import { Button, CardMedia, Stack, SxProps, Theme } from '@mui/material';

import { AppModal } from '@/components';
import { useFetchSasToken } from '@/hooks';
import { IFile } from '@/models';
import { downloadFile, previewPdf } from '@/services';
import { colors } from '@/theme/variables';
import { getFileType, getFileUrlWithValidToken } from '@/utils';

interface FilePreviewModalProps {
  open: boolean;
  file?: IFile;
  onClose: () => void;
  sasToken?: string;
  sx?: SxProps<Theme>;
  readOnly?: boolean;
}

const FilePreviewModal = ({
  open,
  file,
  onClose,
  sasToken: providedSasToken,
  sx,
  readOnly = false,
}: FilePreviewModalProps) => {
  const fetchedSasToken = useFetchSasToken([file]);
  const sasToken = providedSasToken || fetchedSasToken;

  if (!file) return null;

  const fileType = getFileType(file.mimeType);

  const fileUrl = getFileUrlWithValidToken(file, sasToken);

  const previewContent: Record<string, ReactNode> = {
    video: (
      <CardMedia
        component="video"
        sx={{ maxHeight: '600px' }}
        src={fileUrl}
        controls
      />
    ),
    image: (
      <Stack>
        <img
          src={fileUrl}
          alt={file.name}
          loading="lazy"
          style={{
            maxHeight: '590px',
            objectFit: 'contain',
          }}
        />
      </Stack>
    ),
    audio: (
      <Stack
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          backgroundColor: colors.grey[10],
          width: '100%',
          borderRadius: '8px',
        }}
      >
        <CardMedia
          component="audio"
          src={fileUrl}
          controls
          sx={{ width: '100%' }}
        />
      </Stack>
    ),
    document: (
      <Stack
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '176px',
          backgroundColor: colors.grey[10],
          width: '100%',
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={() => previewPdf(file)}
        >
          Open preview in new tab
        </Button>
      </Stack>
    ),
  };

  const onDownload = () => downloadFile(file);

  return (
    <AppModal open={open} title={file.name} onClose={onClose} sx={sx}>
      <Stack sx={{ display: 'flex', alignItems: 'center' }}>
        {previewContent[fileType]}
        {!readOnly && (
          <Button
            sx={{ mt: 2 }}
            size="medium"
            variant="contained"
            color="error"
            onClick={onDownload}
          >
            Download
          </Button>
        )}
        {file.description && (
          <Stack
            sx={{
              mt: 2,
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            {file.description}
          </Stack>
        )}
      </Stack>
    </AppModal>
  );
};

export default FilePreviewModal;
